import { AccordionDetails, Box, Button, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material'
import { useMemo, useState } from 'react'
import { formatDateTime, convertToLabelValue } from '../../../../app/utils'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetAllLoadTemplatesQuery, useGetCarriersQuery } from '../../../../app/redux-fetch/apiOrders'
import StyledAccordion from '../../../../components/Accordions/StyledAccordion'
import StyledAccordionSummary from '../../../../components/Accordions/StyledAccordionSummary'
import ActionButton from '../../../../components/Buttons/ActionButton'
import DefaultSpacingGrid from '../../../../components/Containers/DefaultSpacingGrid'
import FormDatePickerInput from '../../../../components/Forms/FormDatePickerInput'
import FormInput from '../../../../components/Forms/FormInput'
import FormSelect from '../../../../components/Forms/FormSelect'
import { AccordionHeader } from '../../../../components/Headers/styles'
import commonMessages from '../../../../components/Messages/commonMessages'
import InfoDisplayItem from '../../../../components/Typographies/InfoDisplayItem'
import { LoadType, LocationType } from '../../../../types/common'
import { ILoadTemplate } from '../../../LoadTemplate/types'
import { useOrderFormContext } from '../../context/OrderFormContext'
import { useLocationModal } from '../../hooks/useLocationModal'
import messages from '../messages'
import { IShipmentFormInput } from '../schema'


interface EmptyContainerRequestProps {
    consignmentIndex: number
    requestIndex: number
    onRemove?: () => void
    disabled?: boolean;
}

const EmptyContainerRequest: React.FC<EmptyContainerRequestProps> = ({
    consignmentIndex,
    requestIndex,
    onRemove,
    disabled = false
}) => {
    const { setValue, watch, control } = useFormContext()
    const [expanded, setExpanded] = useState(true)
    const { formatMessage } = useIntl()
    const { variant } = useOrderFormContext()

    const handleToggleAccordion = () => {
        setExpanded((prev) => !prev)
    }

    const basePath = `bookings.${consignmentIndex}.containerCollections.${requestIndex}`;

    const customerId = watch('customerId')
    const locationId = watch(`${basePath}.locationId`);
    const carrierOption = watch(`${basePath}.carrierOption`)
    const tenantId = watch('tenantId')

    const {
        locations,
        openModal,
        ModalComponent,
    } = useLocationModal({
        onSave: (id) => {
            setValue(`${basePath}.locationId`, id)
        },
        customerId,
        locationTypeId: LocationType.Terminal,
        oldLocations: locationId ? [locationId] : [],
    })

    const { data: packagingTemplates = [] } = useGetAllLoadTemplatesQuery({
        customerId,
    })

    const { data: carries = [] } = useGetCarriersQuery({ tenantId: tenantId, customerId: customerId })

    const palletPackagingTemplatesOptions = useMemo(
        () =>
            convertToLabelValue(
                packagingTemplates
                    .filter((x: ILoadTemplate) => x.loadTypeId === LoadType.container)
                    .map((x: ILoadTemplate) => ({ name: x.name, id: x.id }))
            ),
        [packagingTemplates]
    )

    const terminalOptions = useMemo(
        () =>
            convertToLabelValue(
                locations.map((location) => ({
                    name: location.name,
                    id: location.id,
                }))
            ),
        [locations]
    )

    const carrierOptions = useMemo(
        () =>
            convertToLabelValue(
                carries.filter(c => c.transportModeId === 2).map((carrier) => ({
                    name: carrier.name,
                    id: carrier.id,
                }))
            ),
        [carries]
    )

    const loadTemplateId = watch(`${basePath}.loadTemplateId`);
    const quantity = watch(`${basePath}.quantity`);
    const carrierId = watch(`${basePath}.carrierId`);
    const croNumber = watch(`${basePath}.croNumber`);
    const croValidFrom = watch(`${basePath}.croValidFrom`);
    const croValidTo = watch(`${basePath}.croValidTo`);

    const getLoadTemplateLabel = () => {
        const template = packagingTemplates.find((x: ILoadTemplate) => x.id === loadTemplateId);
        return template ? template.name : 'N/A';
    };
    const getCarrierLabel = () => {
        const carrier = carries.find((c) => c.id === carrierId);
        return carrier ? carrier.name : 'N/A';
    };
    const getLocationLabel = () => {
        const location = locations.find((l) => l.id === locationId);
        return location ? location.name : 'N/A';
    };

    return (
        <>
            {ModalComponent}
            <StyledAccordion
                defaultExpanded
                sx={{ marginTop: '10px' }}
                onChange={handleToggleAccordion}
            >
                <StyledAccordionSummary
                    index={`emptyContainerRequest-content-${consignmentIndex}-${requestIndex}`}
                    expanded={expanded}
                >
                    <Grid container alignItems="center">
                        <Grid item xs={6} md={8}>
                            <AccordionHeader>
                                {`${formatMessage(messages.emptyContainerRequest)} ${requestIndex + 1}`}
                            </AccordionHeader>
                        </Grid>
                        <Grid item xs={6} md={4} sx={{ textAlign: 'right' }}>
                            {onRemove && !disabled && variant !== 'summary' && (
                                <Button
                                    size='small'
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onRemove();
                                    }}
                                    sx={{ textTransform: 'none', p: 0, mb: '6px', color: 'red' }}
                                >
                                    {formatMessage(commonMessages.remove)}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </StyledAccordionSummary>
                <AccordionDetails>
                    {variant === 'summary' ? (
                        <DefaultSpacingGrid container sx={{ mb: '8px' }}>
                            <Grid item xs={12} md={2}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.containerType)}
                                    value={getLoadTemplateLabel() || 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.quantity)}
                                    value={quantity !== undefined ? quantity : 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.shippingLine)}
                                    value={getCarrierLabel() || 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.croNumber)}
                                    value={croNumber || 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.validFrom)}
                                    value={croValidFrom ? formatDateTime(croValidFrom) : 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.validTo)}
                                    value={croValidTo ? formatDateTime(croValidTo) : 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.emptyCollectionTerminal)}
                                    value={getLocationLabel() || 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                        </DefaultSpacingGrid>
                    ) : (
                        <DefaultSpacingGrid container>
                            <Grid item xs={12} md={2}>
                                <FormSelect
                                    name={`${basePath}.loadTemplateId`}
                                    label={formatMessage(messages.containerType)}
                                    options={palletPackagingTemplatesOptions}
                                    required
                                    fullWidth
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <FormInput
                                    name={`${basePath}.quantity`}
                                    label={formatMessage(messages.quantity)}
                                    type='number'
                                    required
                                    numberFormatProps={{
                                        allowOnlyIntegers: true,
                                        minValue: 1,
                                    }}
                                    fullWidth
                                    disabled={disabled}
                                    maxLength={13}
                                />
                            </Grid>
                            <Grid item xs={12} md={9}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Controller
                                        name={`${basePath}.carrierOption` as keyof IShipmentFormInput}
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup
                                                {...field}
                                                row
                                                sx={{ margin: 'auto' }}
                                                onChange={(e) => {
                                                    field.onChange(e)
                                                    const value = e.target.value

                                                    switch (value) {
                                                        case 'noPreferredLine':
                                                        case 'preferredLine':
                                                        case 'preBooked':
                                                            setValue(`${basePath}.shippingLineId`, undefined)
                                                            setValue(`${basePath}.locationId`, undefined)
                                                            setValue(`${basePath}.croNumber`, undefined)
                                                            setValue(`${basePath}.validFrom`, undefined)
                                                            setValue(`${basePath}.validTo`, undefined)
                                                            break;
                                                    }

                                                }}
                                            >
                                                <FormControlLabel
                                                    value='noPreferredLine'
                                                    control={<Radio disabled={disabled} />}
                                                    label={formatMessage(messages.noPreferredShippingLine)}
                                                />
                                                <FormControlLabel
                                                    value='preferredLine'
                                                    control={<Radio disabled={disabled} />}
                                                    label={formatMessage(messages.preferredShippingLine)}
                                                />
                                                <FormControlLabel
                                                    value='preBooked'
                                                    control={<Radio disabled={disabled} />}
                                                    label={formatMessage(messages.preBookedContainerReleaseOrder)}
                                                />
                                            </RadioGroup>
                                        )}
                                    />
                                </Box>
                            </Grid>
                            {(carrierOption === 'preferredLine' || carrierOption === 'preBooked') && (
                                <>
                                    <Grid item xs={12} md={2}>
                                        <FormSelect
                                            name={`${basePath}.carrierId`}
                                            label={formatMessage(messages.shippingLine)}
                                            options={carrierOptions}
                                            required
                                            fullWidth
                                            disabled={disabled}
                                        />
                                    </Grid>
                                </>
                            )}
                            {(carrierOption === 'preBooked') && (
                                <>
                                    <Grid item xs={12} md={2}>
                                        <FormInput
                                            name={`${basePath}.croNumber`}
                                            label={formatMessage(messages.croNumber)}
                                            placeholder={formatMessage(messages.croNumber)}
                                            required
                                            fullWidth
                                            inputPattern={/[^a-zA-Z0-9]/g}
                                            maxLength={50}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <FormDatePickerInput
                                            name={`${basePath}.croValidFrom`}
                                            label={formatMessage(messages.validFrom)}
                                            required
                                            disablePast={false}
                                            minDate={'1970/01/01'}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <FormDatePickerInput
                                            name={`${basePath}.croValidTo`}
                                            required
                                            label={formatMessage(messages.validTo)}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormSelect
                                            name={`${basePath}.locationId`}
                                            label={formatMessage(messages.emptyCollectionTerminal)}
                                            options={terminalOptions}
                                            required
                                            fullWidth
                                            actionLabel={
                                                !disabled ? (
                                                    <ActionButton
                                                        onClick={() => {
                                                            openModal()
                                                        }}
                                                        label={formatMessage(commonMessages.addNew)}
                                                    />
                                                ) : undefined
                                            }
                                        />
                                    </Grid>
                                </>
                            )}
                        </DefaultSpacingGrid>
                    )}
                </AccordionDetails>
            </StyledAccordion>
        </>
    )
}

export default EmptyContainerRequest
